import { useState, useEffect } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { triggerSyncAll, getSyncStatus, SyncProgressData } from "@/api"
import { toaster } from "@/components/ui"

export const useSyncAll = () => {
  const queryClient = useQueryClient()
  const [isPolling, setIsPolling] = useState(false)

  const startMutation = useMutation({
    mutationFn: triggerSyncAll,
    onSuccess: () => setIsPolling(true),
    onError: (error: any) => {
      toaster.error({ title: error.message || "Failed to start sync" })
    },
  })

  const { data: statusData } = useQuery({
    queryKey: ["syncStatus"],
    queryFn: getSyncStatus,
    refetchInterval: isPolling ? 1500 : false,
    enabled: isPolling,
  })

  useEffect(() => {
    if (!isPolling || !statusData) return
    if (!statusData.isSyncing) {
      setIsPolling(false)
      queryClient.invalidateQueries({ queryKey: ["dealPipeline"] })
      const r = statusData.lastResult
      if (r) {
        toaster.create({
          title:
            `Sync complete: ${r.totalUpserted} upserted, ${r.totalSkipped} skipped` +
            (r.totalErrors > 0 ? `, ${r.totalErrors} errors` : ""),
          type: r.totalErrors > 0 ? "warning" : "success",
        })
      }
    }
  }, [statusData, isPolling, queryClient])

  const isPending = isPolling || startMutation.isPending
  const progress: SyncProgressData | null = statusData?.progress ?? null

  return {
    start: startMutation.mutate,
    isPending,
    progress,
  }
}
